import React from "react";
import "./pagination.ui.scss";

interface IPagination {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  // Total rows across all pages; with pageSize it enables the "Showing a–b of N" summary.
  totalItems?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  // "pill" draws the outlined, arrowed buttons the welcome screen uses; the default is the plain admin look.
  variant?: "default" | "pill";
}

const ArrowIcon = ({ direction }: { direction: "left" | "right" }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="pagination_arrow">
    <path d={direction === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} />
  </svg>
);

const Pagination = (props: IPagination) => {
  const {
    currentPage,
    totalPages,
    onPageChange,
    pageSize,
    totalItems,
    pageSizeOptions = [10, 25, 50],
    onPageSizeChange,
    variant = "default",
  } = props;
  const isPill = variant === "pill";

  const lastPage = Math.max(totalPages, 1);
  const isPrevDisabled = currentPage <= 1 || totalPages <= 0;
  const isNextDisabled = currentPage >= lastPage || totalPages <= 0;

  const hasSummary = !!totalItems && !!pageSize && totalItems > 0;
  const rangeStart = hasSummary ? (currentPage - 1) * (pageSize as number) + 1 : 0;
  const rangeEnd = hasSummary ? Math.min(currentPage * (pageSize as number), totalItems as number) : 0;

  return (
    <div className={`pagination${isPill ? " pagination--pill" : ""}`}>
      {hasSummary && (
        <span className="pagination_summary" data-testid="pagination_summary">
          Showing {rangeStart}–{rangeEnd} of {totalItems}
        </span>
      )}
      <button
        type="button"
        data-testid="pagination_prev"
        disabled={isPrevDisabled}
        onClick={() => onPageChange(currentPage - 1)}
      >
        {isPill && <ArrowIcon direction="left" />}
        Prev
      </button>
      <span className="pagination_page_info">
        Page {currentPage} of {lastPage}
      </span>
      <button
        type="button"
        data-testid="pagination_next"
        disabled={isNextDisabled}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
        {isPill && <ArrowIcon direction="right" />}
      </button>
      {onPageSizeChange && (
        <select
          data-testid="pagination_page_size"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default Pagination;
